import { useEffect, useRef, useState } from "react";

/**
 * Address picker with Nominatim (OpenStreetMap) autocomplete.
 * Free, no API key — usage policy requires a referer (browser sends it automatically)
 * and a soft 1 req/sec limit, which we honor with debounce.
 *
 * The component is self-contained: it owns the query input and the dropdown.
 * When the user picks a suggestion, `onPick` fires with the canonical address
 * + coordinates, and the parent stores them.
 */
export type Picked = {
  display_name: string;
  venue_name: string;   // best guess: name/road
  address: string;      // formatted compact address
  lat: number;
  lng: number;
};

type NominatimResult = {
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
  address?: {
    amenity?: string;
    shop?: string;
    leisure?: string;
    building?: string;
    road?: string;
    house_number?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
  };
};

type Props = {
  value: string;
  onChange: (q: string) => void;
  onPick: (p: Picked) => void;
  placeholder?: string;
  /** Bias results to a country (ISO 3166-1 alpha2). Default "il". */
  country?: string;
};

export function AddressAutocomplete({
  value,
  onChange,
  onPick,
  placeholder = "Кафе, парк или адрес — начни печатать",
  country = "il",
}: Props) {
  // Israel-only app — addresses are always in Hebrew. Single language keeps
  // the result consistent (no city name slipping into another language) and
  // matches the map picker. Non-Hebrew readers use the card's Translate button.
  const acceptLang = "he";
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Close on outside click.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced fetch.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const q = value.trim();
    if (q.length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const url =
          `https://nominatim.openstreetmap.org/search` +
          `?format=json&addressdetails=1&limit=5&countrycodes=${country}` +
          `&accept-language=${encodeURIComponent(acceptLang)}` +
          `&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as NominatimResult[];
        setResults(data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, country, acceptLang]);

  function handlePick(r: NominatimResult) {
    const venue =
      r.name ??
      r.address?.amenity ??
      r.address?.shop ??
      r.address?.leisure ??
      r.address?.building ??
      r.address?.road ??
      r.display_name.split(",")[0]?.trim() ??
      "";
    const street = [r.address?.road, r.address?.house_number].filter(Boolean).join(" ");
    const city = r.address?.city ?? r.address?.town ?? r.address?.village ?? "";
    const compact = [street, city].filter(Boolean).join(", ") || r.display_name;
    onChange(venue || compact);
    onPick({
      display_name: r.display_name,
      venue_name: venue || compact,
      address: compact,
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
    });
    setOpen(false);
  }

  return (
    // z-[1200] keeps this above the Leaflet map rendered right below it in
    // CreateEvent — Leaflet panes/controls sit at z 400-1000, so a lower
    // dropdown would be hidden *behind* the map.
    <div ref={wrapRef} className="relative z-[1200]">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="w-full h-13 px-4 text-base bg-ink-50 border border-ink-200 rounded-[var(--radius-md)] text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-brand-purple focus:bg-surface focus:ring-4 focus:ring-brand-purple/10 transition-all duration-150"
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-ink-400">
          …
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-[1200] bg-surface border border-ink-100 rounded-[var(--radius-md)] shadow-[var(--shadow-elevated)] overflow-hidden">
          {results.map((r, i) => (
            <button
              key={`${r.lat}-${r.lon}-${i}`}
              type="button"
              onClick={() => handlePick(r)}
              className="w-full text-left px-4 py-3 hover:bg-ink-50 border-b border-ink-100 last:border-b-0"
            >
              <div className="text-[14px] font-semibold text-ink-900 truncate">
                {r.name ??
                  r.address?.amenity ??
                  r.address?.shop ??
                  r.address?.leisure ??
                  r.address?.road ??
                  r.display_name.split(",")[0]}
              </div>
              <div className="text-[12px] text-ink-500 truncate">{r.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
